import { zodResolver } from '@hookform/resolvers/zod';
import { PlusSquareIcon } from '@phosphor-icons/react';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import z from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/modules/common';
import { Routes } from '@/router/routes';

import { useCreatePrompt } from '../api';

interface NewPromptState {
  categoryId: string;
  categoryName: string;
  content: string;
}

const schema = z.object({
  notes: z.string(),
  content: z.string().min(1, 'Content is required'),
});

type FormValues = z.infer<typeof schema>;

export const NewSystemPromptPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as NewPromptState;

  const { createPromptMutation } = useCreatePrompt();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      notes: '',
      content: state?.content ?? '',
    },
  });

  if (!state?.categoryId) {
    navigate(Routes.SystemPrompts, { replace: true });
    return null;
  }

  const handleSubmit = async (values: FormValues) => {
    try {
      await createPromptMutation.mutateAsync({
        categoryId: state.categoryId,
        content: values.content,
        notes: values.notes,
      });
      navigate(Routes.SystemPrompts, { replace: true });
      toast.success('Prompt created successfully!');
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(handleSubmit)(e);
        }}
      >
        <PageHeader
          title="New System Prompt"
          asChildPage
          backPath={Routes.SystemPrompts}
          actions={[
            <Button
              key="create-prompt"
              type="submit"
              isLoading={createPromptMutation.isPending}
            >
              <PlusSquareIcon />
              Create Prompt
            </Button>,
          ]}
        />

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 max-w-3xl">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-zinc-400">Category</span>
              <span className="text-sm text-zinc-100">{state?.categoryName}</span>
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add notes for other admins..."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Content</FormLabel>
                <FormControl>
                  <Textarea
                    className="min-h-[320px] font-mono text-sm"
                    placeholder="Enter prompt content..."
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </form>
    </Form>
  );
};
